import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./Villaroominventory.css";

const API_BASE = (
  process.env.REACT_APP_API_URL || "https://bmtvendorvendorfinal.onrender.com"
).replace(/\/$/, "");

const KEY_VILLA = "bmtVillaInvVilla";
const KEY_ROOM = "bmtVillaInvRoom";
const MAX_VIEW_DAYS = 92;
const MAX_BULK_DAYS = 366;

const WEEKDAYS = [
  ["Mon", 1], ["Tue", 2], ["Wed", 3], ["Thu", 4], ["Fri", 5], ["Sat", 6], ["Sun", 0],
];

const QUICK_RANGES = [7, 14, 30, 60, 90];

/* ---------- date helpers (all keys are YYYY-MM-DD, UTC math) ---------- */

const pad = (n) => String(n).padStart(2, "0");
const toKey = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const fromKey = (k) => {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const addDays = (k, n) => {
  const d = fromKey(k);
  d.setUTCDate(d.getUTCDate() + n);
  return toKey(d);
};
const todayKey = () => {
  const n = new Date();
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
};
const diffDays = (a, b) => Math.round((fromKey(b) - fromKey(a)) / 86400000);
const rangeKeys = (from, to) => {
  const out = [];
  for (let k = from; k <= to; k = addDays(k, 1)) out.push(k);
  return out;
};
const fmtDate = (k) =>
  fromKey(k).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
const fmtDay = (k) =>
  fromKey(k).toLocaleDateString("en-IN", { weekday: "short", timeZone: "UTC" });
const isWeekend = (k) => [0, 6].includes(fromKey(k).getUTCDay());
const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

const readSession = (key) => {
  try { return sessionStorage.getItem(key) || ""; } catch { return ""; }
};
const writeSession = (key, value) => {
  try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
};

async function apiRequest(path, options = {}) {
  const token = localStorage.getItem("vendorToken");

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || data.success === false) {
    throw new Error(
      data.message ||
        (response.status === 401
          ? "Session expired. Please log in again."
          : `Request failed (${response.status})`)
    );
  }
  return data;
}

const isWhole = (v) => v !== "" && Number.isInteger(Number(v)) && Number(v) >= 0;

/* ---------- component ---------- */

export default function VillaRoomInventory() {
  const [villas, setVillas] = useState([]);
  const [villaId, setVillaId] = useState(() => readSession(KEY_VILLA));
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState(() => readSession(KEY_ROOM));
  const [booting, setBooting] = useState(true);
  const [roomsLoading, setRoomsLoading] = useState(false);

  const [from, setFrom] = useState(todayKey());
  const [to, setTo] = useState(() => addDays(todayKey(), 29));

  const [records, setRecords] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [filter, setFilter] = useState("all");

  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState({ total: "", blocked: "", closed: false });
  const [savingKey, setSavingKey] = useState(null);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({
    from: "", to: "", total: "1", blocked: "0", closed: false, days: [0, 1, 2, 3, 4, 5, 6],
  });
  const [bulkRun, setBulkRun] = useState(null);

  const room = rooms.find((r) => r._id === roomId) || null;
  const villa = villas.find((v) => v._id === villaId) || null;

  const rangeError = useMemo(() => {
    if (!from || !to) return "Select both dates.";
    if (from > to) return "The start date must be before the end date.";
    if (diffDays(from, to) + 1 > MAX_VIEW_DAYS) return `Please select at most ${MAX_VIEW_DAYS} days at a time.`;
    return "";
  }, [from, to]);

  const invPath = useCallback(
    (suffix = "") =>
      `/api/vendor/villas/${encodeURIComponent(villaId)}/rooms/${encodeURIComponent(roomId)}/inventory${suffix}`,
    [villaId, roomId]
  );

  /* load villas */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiRequest("/api/vendor/villas/my-villas");
        if (cancelled) return;
        const list = data.villas || data.data || [];
        setVillas(list);
        setVillaId((cur) => (cur && list.some((v) => v._id === cur) ? cur : list[0]?._id || ""));
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* load rooms of selected villa */
  useEffect(() => {
    if (!villaId) { setRooms([]); setRoomId(""); return undefined; }
    writeSession(KEY_VILLA, villaId);
    let cancelled = false;
    setRoomsLoading(true);
    (async () => {
      try {
        const data = await apiRequest(`/api/vendor/villas/${encodeURIComponent(villaId)}/rooms`);
        if (cancelled) return;
        const list = Array.isArray(data.rooms) ? data.rooms : [];
        setRooms(list);
        setRoomId((cur) => (cur && list.some((r) => r._id === cur) ? cur : list[0]?._id || ""));
      } catch (err) {
        if (!cancelled) { setRooms([]); setRoomId(""); setError(err.message); }
      } finally {
        if (!cancelled) setRoomsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [villaId]);

  useEffect(() => {
    if (roomId) writeSession(KEY_ROOM, roomId);
  }, [roomId]);

  /* load inventory + summary */
  const loadInventory = useCallback(
    async (silent = false) => {
      if (!villaId || !roomId || rangeError) return;
      if (!silent) setLoading(true);
      try {
        const query = `?from=${from}&to=${to}`;
        const [inv, sum] = await Promise.all([
          apiRequest(invPath(query)),
          apiRequest(invPath(`/summary${query}`)).catch(() => null),
        ]);

        const map = {};
        (inv.inventory || []).forEach((item) => {
          map[String(item.date).slice(0, 10)] = item;
        });
        setRecords(map);
        setSummary(sum?.summary || null);
      } catch (err) {
        setError(err.message || "Unable to load inventory.");
      } finally {
        setLoading(false);
      }
    },
    [villaId, roomId, from, to, rangeError, invPath]
  );

  useEffect(() => {
    setEditingKey(null);
    setRecords({});
    setSummary(null);
    loadInventory();
  }, [loadInventory]);

  /* rows for the table */
  const rows = useMemo(() => {
    if (rangeError) return [];
    const defaultTotal = Number(room?.totalRooms || 0);
    return rangeKeys(from, to).map((key) => {
      const rec = records[key];
      const total = rec ? rec.totalRooms : defaultTotal;
      const booked = rec ? rec.bookedRooms : 0;
      const blocked = rec ? rec.blockedRooms : 0;
      const closed = rec ? Boolean(rec.isClosed) : false;
      const available = closed ? 0 : Math.max(0, total - booked - blocked);
      let status = "unset";
      if (rec) status = closed ? "closed" : available === 0 ? "soldout" : "open";
      return { key, rec, total, booked, blocked, closed, available, status };
    });
  }, [records, from, to, rangeError, room]);

  const visibleRows = useMemo(
    () => (filter === "all" ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter]
  );

  const counts = useMemo(() => {
    const c = { all: rows.length, open: 0, closed: 0, soldout: 0, unset: 0 };
    rows.forEach((r) => { c[r.status] += 1; });
    return c;
  }, [rows]);

  /* period navigation */
  const spanDays = rangeError ? 30 : diffDays(from, to) + 1;
  const setSpan = (n) => setTo(addDays(from, n - 1));
  const shift = (dir) => {
    setFrom(addDays(from, dir * spanDays));
    setTo(addDays(to, dir * spanDays));
  };
  const goToday = () => {
    const t = todayKey();
    setFrom(t);
    setTo(addDays(t, spanDays - 1));
  };

  /* inline edit */
  const startEdit = (row) => {
    setError("");
    setNotice("");
    setEditingKey(row.key);
    setDraft({ total: String(row.total), blocked: String(row.blocked), closed: row.closed });
  };

  const saveRow = async (row) => {
    if (!isWhole(draft.total)) return setError("Total rooms must be a whole number (0 or more).");
    if (!isWhole(draft.blocked)) return setError("Blocked rooms must be a whole number (0 or more).");
    const total = Number(draft.total);
    const blocked = Number(draft.blocked);
    if (blocked > total) return setError("Blocked rooms cannot exceed total rooms.");
    if (row.booked + blocked > total)
      return setError(`${row.booked} room(s) are already booked on this date, so total cannot be less than booked + blocked.`);

    setSavingKey(row.key);
    setError("");
    try {
      const data = await apiRequest(invPath(), {
        method: "POST",
        body: JSON.stringify({ date: row.key, totalRooms: total, blockedRooms: blocked, isClosed: Boolean(draft.closed) }),
      });
      setRecords((prev) => ({ ...prev, [row.key]: data.inventory }));
      setEditingKey(null);
      setNotice(`Inventory saved for ${fmtDate(row.key)}.`);
      loadInventory(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingKey(null);
    }
  };

  const toggleClosed = async (row) => {
    setSavingKey(row.key);
    setError("");
    setNotice("");
    try {
      const data = await apiRequest(invPath(), {
        method: "POST",
        body: JSON.stringify({
          date: row.key,
          totalRooms: row.total,
          blockedRooms: row.blocked,
          isClosed: !row.closed,
        }),
      });
      setRecords((prev) => ({ ...prev, [row.key]: data.inventory }));
      setNotice(`${fmtDate(row.key)} ${row.closed ? "reopened" : "closed"} for booking.`);
      loadInventory(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingKey(null);
    }
  };

  /* bulk update */
  const openBulk = () => {
    setBulk({
      from, to,
      total: String(room?.totalRooms || 1),
      blocked: "0",
      closed: false,
      days: [0, 1, 2, 3, 4, 5, 6],
    });
    setBulkOpen(true);
  };

  const bulkDates = useMemo(() => {
    if (!bulk.from || !bulk.to || bulk.from > bulk.to) return [];
    if (diffDays(bulk.from, bulk.to) + 1 > MAX_BULK_DAYS) return [];
    return rangeKeys(bulk.from, bulk.to).filter((k) => bulk.days.includes(fromKey(k).getUTCDay()));
  }, [bulk.from, bulk.to, bulk.days]);

  const bulkProblem = (() => {
    if (!bulk.from || !bulk.to) return "Select both dates.";
    if (bulk.from > bulk.to) return "The start date must be before the end date.";
    if (diffDays(bulk.from, bulk.to) + 1 > MAX_BULK_DAYS) return `Maximum ${MAX_BULK_DAYS} days per bulk update.`;
    if (!bulk.days.length) return "Select at least one weekday.";
    if (!isWhole(bulk.total)) return "Total rooms must be a whole number.";
    if (!isWhole(bulk.blocked)) return "Blocked rooms must be a whole number.";
    if (Number(bulk.blocked) > Number(bulk.total)) return "Blocked rooms cannot exceed total rooms.";
    return "";
  })();

  const runBulk = async () => {
    if (bulkProblem || !bulkDates.length) return;
    setError("");
    setNotice("");

    const dates = [...bulkDates];
    const payload = {
      totalRooms: Number(bulk.total),
      blockedRooms: Number(bulk.blocked),
      isClosed: Boolean(bulk.closed),
    };

    let index = 0;
    let done = 0;
    const failed = [];
    setBulkRun({ done: 0, total: dates.length, failed: 0 });

    const worker = async () => {
      while (index < dates.length) {
        const key = dates[index];
        index += 1;
        try {
          await apiRequest(invPath(), { method: "POST", body: JSON.stringify({ date: key, ...payload }) });
        } catch (err) {
          failed.push({ key, message: err.message });
        }
        done += 1;
        setBulkRun({ done, total: dates.length, failed: failed.length });
      }
    };

    await Promise.all(Array.from({ length: Math.min(5, dates.length) }, worker));
    setBulkRun(null);
    await loadInventory(true);

    const ok = dates.length - failed.length;
    if (failed.length) {
      setError(`${failed.length} date(s) could not be updated (e.g. ${fmtDate(failed[0].key)}: ${failed[0].message}).`);
    }
    if (ok) setNotice(`Inventory updated for ${ok} date(s).`);
    if (!failed.length) setBulkOpen(false);
  };

  const toggleBulkDay = (n) =>
    setBulk((p) => ({
      ...p,
      days: p.days.includes(n) ? p.days.filter((d) => d !== n) : [...p.days, n],
    }));

  /* ---------- guards ---------- */

  if (booting) {
    return (
      <div className="vi-page">
        <div className="vi-center"><span className="vi-spinner" /> Loading your villas...</div>
      </div>
    );
  }

  if (!villas.length) {
    return (
      <div className="vi-page">
        {error && <div className="vi-alert error"><span>{error}</span><button onClick={() => setError("")}>×</button></div>}
        <div className="vi-empty">
          <div className="vi-empty-icon">⌂</div>
          <h3>Add a villa first</h3>
          <p>Inventory is managed per room. Create a villa and add rooms, then manage dates here.</p>
        </div>
      </div>
    );
  }

  const today = todayKey();

  return (
    <div className="vi-page">
      <header className="vi-header">
        <div>
          <div className="vi-kicker">PROPERTY MANAGEMENT / VILLAS</div>
          <h1>Rates & inventory</h1>
          <p>Control date-wise availability, blocked rooms and closed dates for each room.</p>
        </div>
        <div className="vi-header-actions">
          <button className="vi-btn secondary" onClick={() => loadInventory()} disabled={loading || !roomId}>↻ Refresh</button>
          <button className="vi-btn primary" onClick={openBulk} disabled={!roomId}>⚙ Bulk update</button>
        </div>
      </header>

      {/* selectors */}
      <section className="vi-controls">
        <div className="vi-control">
          <label htmlFor="vi-villa">Villa</label>
          <select id="vi-villa" value={villaId} onChange={(e) => setVillaId(e.target.value)}>
            {villas.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
          </select>
        </div>

        <div className="vi-control">
          <label htmlFor="vi-room">Room</label>
          <select id="vi-room" value={roomId} onChange={(e) => setRoomId(e.target.value)} disabled={roomsLoading || !rooms.length}>
            {rooms.length === 0 && <option value="">{roomsLoading ? "Loading..." : "No rooms"}</option>}
            {rooms.map((r) => (
              <option key={r._id} value={r._id}>
                {r.roomName}{r.roomNumber ? ` (${r.roomNumber})` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="vi-control small">
          <label htmlFor="vi-from">From</label>
          <input id="vi-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="vi-control small">
          <label htmlFor="vi-to">To</label>
          <input id="vi-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </div>
      </section>

      {notice && <div className="vi-alert success"><span>✓ {notice}</span><button onClick={() => setNotice("")}>×</button></div>}
      {error && <div className="vi-alert error" role="alert"><span>{error}</span><button onClick={() => setError("")}>×</button></div>}

      {!rooms.length && !roomsLoading ? (
        <div className="vi-empty">
          <div className="vi-empty-icon">▭</div>
          <h3>No rooms in {villa?.name || "this villa"}</h3>
          <p>Add a room from “Villa Rooms” first, then come back to manage its inventory.</p>
        </div>
      ) : (
        <>
          {/* summary strip */}
          <section className="vi-stats">
            <div className="vi-stat"><small>Room nights available</small><strong>{summary ? summary.availableRoomNights : "—"}</strong></div>
            <div className="vi-stat"><small>Booked</small><strong>{summary ? summary.bookedRooms : "—"}</strong></div>
            <div className="vi-stat"><small>Blocked</small><strong>{summary ? summary.blockedRooms : "—"}</strong></div>
            <div className="vi-stat"><small>Closed dates</small><strong>{summary ? summary.closedDates : "—"}</strong></div>
            <div className="vi-stat"><small>Dates configured</small><strong>{summary ? `${summary.daysWithInventory} / ${rows.length}` : "—"}</strong></div>
          </section>

          {/* bulk update panel (inline, not a popup) */}
          {bulkOpen && (
            <section className="vi-bulk">
              <div className="vi-bulk-head">
                <div>
                  <span className="vi-kicker">BULK UPDATE</span>
                  <h2>Update multiple dates at once</h2>
                  <p>Applies to “{room?.roomName}”. Existing bookings are never overwritten.</p>
                </div>
                <button className="vi-btn secondary" onClick={() => setBulkOpen(false)} disabled={Boolean(bulkRun)}>Close</button>
              </div>

              <div className="vi-bulk-grid">
                <div className="vi-field">
                  <label>From date</label>
                  <input type="date" value={bulk.from} onChange={(e) => setBulk((p) => ({ ...p, from: e.target.value }))} />
                </div>
                <div className="vi-field">
                  <label>To date</label>
                  <input type="date" value={bulk.to} min={bulk.from} onChange={(e) => setBulk((p) => ({ ...p, to: e.target.value }))} />
                </div>
                <div className="vi-field">
                  <label>Total rooms</label>
                  <input type="number" min="0" value={bulk.total} onChange={(e) => setBulk((p) => ({ ...p, total: e.target.value }))} />
                </div>
                <div className="vi-field">
                  <label>Blocked rooms</label>
                  <input type="number" min="0" value={bulk.blocked} onChange={(e) => setBulk((p) => ({ ...p, blocked: e.target.value }))} />
                </div>
                <div className="vi-field">
                  <label>Booking status</label>
                  <select value={bulk.closed ? "closed" : "open"} onChange={(e) => setBulk((p) => ({ ...p, closed: e.target.value === "closed" }))}>
                    <option value="open">Open for booking</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>

              <div className="vi-days">
                <span>Apply on</span>
                {WEEKDAYS.map(([label, n]) => (
                  <button
                    type="button"
                    key={label}
                    className={bulk.days.includes(n) ? "on" : ""}
                    onClick={() => toggleBulkDay(n)}
                  >
                    {label}
                  </button>
                ))}
                <button type="button" className="link" onClick={() => setBulk((p) => ({ ...p, days: [0, 1, 2, 3, 4, 5, 6] }))}>All</button>
                <button type="button" className="link" onClick={() => setBulk((p) => ({ ...p, days: [1, 2, 3, 4, 5] }))}>Weekdays</button>
                <button type="button" className="link" onClick={() => setBulk((p) => ({ ...p, days: [0, 6] }))}>Weekends</button>
              </div>

              {bulkProblem && <p className="vi-bulk-problem">{bulkProblem}</p>}

              <div className="vi-bulk-foot">
                <span className="vi-muted">
                  {bulkProblem ? "" : `This will update ${bulkDates.length} date(s).`}
                </span>
                <div className="vi-bulk-actions">
                  {bulkRun && (
                    <span className="vi-progress-text">
                      Updating {bulkRun.done} / {bulkRun.total}
                      {bulkRun.failed ? ` · ${bulkRun.failed} failed` : ""}
                    </span>
                  )}
                  <button className="vi-btn primary" onClick={runBulk} disabled={Boolean(bulkProblem) || !bulkDates.length || Boolean(bulkRun)}>
                    {bulkRun ? "Updating..." : "Apply to dates"}
                  </button>
                </div>
              </div>
              {bulkRun && (
                <div className="vi-bar"><div style={{ width: `${(bulkRun.done / bulkRun.total) * 100}%` }} /></div>
              )}
            </section>
          )}

          {/* inventory table */}
          <section className="vi-panel">
            <div className="vi-toolbar">
              <div>
                <h2>Date-wise inventory</h2>
                <p>
                  {room?.roomName}
                  {room ? ` · default ${room.totalRooms} unit(s) · ${money(room.pricePerNight)} / night` : ""}
                </p>
              </div>

              <div className="vi-period">
                <button className="vi-btn secondary icon" onClick={() => shift(-1)} disabled={Boolean(rangeError)} aria-label="Previous period">‹</button>
                <button className="vi-btn secondary" onClick={goToday}>Today</button>
                <button className="vi-btn secondary icon" onClick={() => shift(1)} disabled={Boolean(rangeError)} aria-label="Next period">›</button>
                <div className="vi-ranges">
                  {QUICK_RANGES.map((n) => (
                    <button key={n} className={spanDays === n ? "on" : ""} onClick={() => setSpan(n)}>{n}d</button>
                  ))}
                </div>
              </div>
            </div>

            <div className="vi-filters">
              {[
                ["all", "All"], ["open", "Open"], ["soldout", "Sold out"], ["closed", "Closed"], ["unset", "Not set"],
              ].map(([key, label]) => (
                <button key={key} className={filter === key ? "on" : ""} onClick={() => setFilter(key)}>
                  {label} <span>{counts[key]}</span>
                </button>
              ))}
            </div>

            {rangeError ? (
              <div className="vi-empty compact"><p>{rangeError}</p></div>
            ) : loading ? (
              <div className="vi-center"><span className="vi-spinner" /> Loading inventory...</div>
            ) : visibleRows.length === 0 ? (
              <div className="vi-empty compact"><p>No dates match this filter.</p></div>
            ) : (
              <div className="vi-table-wrap">
                <table className="vi-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th className="num">Total</th>
                      <th className="num">Booked</th>
                      <th className="num">Blocked</th>
                      <th className="num">Available</th>
                      <th>Status</th>
                      <th className="right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => {
                      const editing = editingKey === row.key;
                      const saving = savingKey === row.key;
                      const liveAvail = editing
                        ? draft.closed
                          ? 0
                          : Math.max(0, Number(draft.total || 0) - row.booked - Number(draft.blocked || 0))
                        : row.available;

                      return (
                        <tr
                          key={row.key}
                          className={`${isWeekend(row.key) ? "weekend" : ""} ${row.key < today ? "past" : ""} ${editing ? "editing" : ""}`}
                        >
                          <td>
                            <div className="vi-date">
                              <strong>{fmtDate(row.key)}</strong>
                              <small>
                                {fmtDay(row.key)}
                                {row.key === today && <b className="vi-today">Today</b>}
                              </small>
                            </div>
                          </td>

                          <td className="num">
                            {editing ? (
                              <input className="vi-cell-input" type="number" min="0" value={draft.total}
                                onChange={(e) => setDraft((p) => ({ ...p, total: e.target.value }))} />
                            ) : row.total}
                          </td>
                          <td className="num">{row.booked}</td>
                          <td className="num">
                            {editing ? (
                              <input className="vi-cell-input" type="number" min="0" value={draft.blocked}
                                onChange={(e) => setDraft((p) => ({ ...p, blocked: e.target.value }))} />
                            ) : row.blocked}
                          </td>
                          <td className="num"><strong className={liveAvail === 0 ? "zero" : "ok"}>{liveAvail}</strong></td>

                          <td>
                            {editing ? (
                              <select className="vi-cell-select" value={draft.closed ? "closed" : "open"}
                                onChange={(e) => setDraft((p) => ({ ...p, closed: e.target.value === "closed" }))}>
                                <option value="open">Open</option>
                                <option value="closed">Closed</option>
                              </select>
                            ) : (
                              <span className={`vi-status ${row.status}`}>
                                {{ open: "Open", closed: "Closed", soldout: "Sold out", unset: "Not set" }[row.status]}
                              </span>
                            )}
                          </td>

                          <td>
                            <div className="vi-actions">
                              {editing ? (
                                <>
                                  <button className="save" onClick={() => saveRow(row)} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
                                  <button onClick={() => setEditingKey(null)} disabled={saving}>Cancel</button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => startEdit(row)} disabled={saving || Boolean(editingKey)}>Edit</button>
                                  <button
                                    className={row.closed ? "" : "danger"}
                                    onClick={() => toggleClosed(row)}
                                    disabled={saving || Boolean(editingKey)}
                                  >
                                    {saving ? "..." : row.closed ? "Reopen" : "Close"}
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <p className="vi-footnote">
              “Not set” dates use the room's default of {room?.totalRooms ?? 0} unit(s) until you save them.
              Booked rooms are controlled by reservations and cannot be edited here.
            </p>
          </section>
        </>
      )}
    </div>
  );
}